import { apiFetch, type ApiError } from "./client";

/**
 * Mail integration client — two surfaces:
 *
 * 1. the caller's OWN mailbox connection (`/api/mail/connection`), which is
 * strictly personal: the API has no id path and no `?userId=`, so there is
 * nothing to pass here either;
 * 2. sending (`/api/mail/send`) and the correspondence timeline
 * (`/api/communications`).
 *
 * The app-password is write-only end to end: it is sent on `save`, never
 * returned. `hasSecret` is the only thing the API says about it.
 */

/** Mirror of the backend MailConnectionStatus enum. */
export type MailConnectionStatus = "UNVERIFIED" | "OK" | "ERROR";

export type MailConnection = {
  id: string;
  email: string;
  displayName: string | null;
  smtpHost: string;
  smtpPort: number;
  smtpStarttls: boolean;
  imapHost: string;
  imapPort: number;
  imapSsl: boolean;
  username: string;
  /** True when an app-password is stored. The secret itself is never returned. */
  hasSecret: boolean;
  status: MailConnectionStatus;
  lastError: string | null;
  lastCheckedAt: string | null;
  updatedAt: string;
};

/** Upsert body. `secret` is required when creating, optional on edit (omit to keep the stored one). */
export type MailConnectionUpsert = {
  email: string;
  displayName?: string | null;
  smtpHost: string;
  smtpPort?: number | null;
  smtpStarttls?: boolean | null;
  imapHost: string;
  imapPort?: number | null;
  imapSsl?: boolean | null;
  username: string;
  secret?: string | null;
};

export type CommunicationDirection = "OUT" | "IN";
export type CommunicationStatus = "QUEUED" | "SENT" | "FAILED" | "RECEIVED";

export type CommunicationAttachment = {
  id: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
};

export type Communication = {
  id: string;
  direction: CommunicationDirection;
  leadId: string | null;
  customerCompanyId: string | null;
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  fromEmail: string;
  /** Comma-separated recipient list, as stored server-side. */
  toEmails: string;
  ccEmails: string | null;
  status: CommunicationStatus;
  error: string | null;
  sentBy: string | null;
  sentByName: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  /** First click on a tracked link in this message (null = no click recorded). */
  clickedAt: string | null;
  createdAt: string;
  attachments: CommunicationAttachment[];
  /** True when this is someone else's correspondence: body/subject/addresses are withheld. */
  redacted: boolean;
  /** Survives redaction so the timeline can still show a paperclip. */
  hasAttachments: boolean;
};

/** POST /api/mail/send — at least one of leadId / customerCompanyId is required. */
export type MailSendRequest = {
  leadId?: string | null;
  customerCompanyId?: string | null;
  to: string[];
  cc?: string[] | null;
  subject: string;
  bodyText: string;
  bodyHtml?: string | null;
  /** Renders the offer PDF server-side and attaches it as Ponuda_<broj>.pdf. */
  offerId?: string | null;
};

/**
 * Bankom's own mail server — the default for every rep, since bankom.rs
 * publishes MX `mail.bankom.rs` and an SPF record that only authorises that
 * host. Sending through it is what keeps `@bankom.rs` mail out of spam.
 */
export const BANKOM_PRESET = {
  smtpHost: "mail.bankom.rs",
  smtpPort: 587,
  smtpStarttls: true,
  imapHost: "mail.bankom.rs",
  imapPort: 993,
  imapSsl: true,
} as const;

/** Gmail, for a rep whose mailbox lives there instead. */
export const GMAIL_PRESET = {
  smtpHost: "smtp.gmail.com",
  smtpPort: 587,
  smtpStarttls: true,
  imapHost: "imap.gmail.com",
  imapPort: 993,
  imapSsl: true,
} as const;

/** Backend defaults — used to seed a blank form. */
export const MAIL_DEFAULTS = {
  smtpPort: 587,
  smtpStarttls: true,
  imapPort: 993,
  imapSsl: true,
} as const;

/** Split a free-typed recipient field ("a@x.rs, b@y.rs; c@z.rs") into addresses. */
export function parseAddresses(input: string): string[] {
  return input
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const mailApi = {
  /** The caller's own connection, or null when they have never connected one (404). */
  getConnection: async (): Promise<MailConnection | null> => {
    try {
      return await apiFetch<MailConnection>("/mail/connection");
    } catch (err) {
      if ((err as ApiError).status === 404) return null;
      throw err;
    }
  },
  saveConnection: (body: MailConnectionUpsert) =>
    apiFetch<MailConnection>("/mail/connection", { method: "PUT", body }),
  /** Real SMTP auth + IMAP login probe; writes status OK/ERROR + lastError. */
  testConnection: () => apiFetch<MailConnection>("/mail/connection/test", { method: "POST" }),
  deleteConnection: () => apiFetch<void>("/mail/connection", { method: "DELETE" }),

  send: (body: MailSendRequest) => apiFetch<Communication>("/mail/send", { method: "POST", body }),

  timeline: (target: { leadId?: string | null; companyId?: string | null; offerId?: string | null }) => {
    const q = target.leadId
      ? `leadId=${encodeURIComponent(target.leadId)}`
      : target.offerId
        ? `offerId=${encodeURIComponent(target.offerId)}`
        : `companyId=${encodeURIComponent(target.companyId ?? "")}`;
    return apiFetch<Communication[]>(`/communications?${q}`);
  },

  /** offerId -> last-send timestamp, for the "mailed" marker on the offers board. */
  mailedOffers: () => apiFetch<Record<string, string>>("/communications/mailed-offers"),

  /** Short-lived download URL for one attachment (owner-only, 5 min). */
  attachmentUrl: (communicationId: string, attachmentId: string) =>
    apiFetch<{ url: string }>(`/communications/${communicationId}/attachments/${attachmentId}`),
};
